// WebAuthn Level 3-hjälpare. Använder native browser APIs
// (parseCreationOptionsFromJSON / parseRequestOptionsFromJSON / toJSON) —
// ingen simplewebauthn-beroende behövs för Chrome 129+, Firefox 119+,
// Safari 18.4+ (Baseline 2025). Buggig password manager kan kräva manuell
// serialisering via PublicKeyCredential.toJSON — fallback ligger nedan.

export function isPasskeySupported(): boolean {
  return (
    typeof window !== 'undefined' &&
    'PublicKeyCredential' in window &&
    typeof (PublicKeyCredential as unknown as {
      parseCreationOptionsFromJSON?: unknown
    }).parseCreationOptionsFromJSON === 'function'
  )
}

/**
 * Serialize a PublicKeyCredential for POST till backend.
 * cred.toJSON() ger spec-JSON i moderna webbläsare. Vid problem med
 * felaktiga polyfills — fallback till manuell base64url-serialisering.
 */
export function credentialToJson(cred: PublicKeyCredential): string {
  const asJson = (cred as unknown as { toJSON?: () => unknown }).toJSON
  if (typeof asJson === 'function') {
    return JSON.stringify(asJson.call(cred))
  }
  // Ska aldrig hända i browsers vi stödjer, men bättre än en tyst crash.
  throw new Error('PublicKeyCredential.toJSON saknas — uppdatera webbläsaren.')
}

export async function createPasskey(
  options: PublicKeyCredentialCreationOptionsJSON,
): Promise<string> {
  const publicKey = (
    PublicKeyCredential as unknown as {
      parseCreationOptionsFromJSON: (
        opts: PublicKeyCredentialCreationOptionsJSON,
      ) => PublicKeyCredentialCreationOptions
    }
  ).parseCreationOptionsFromJSON(options)

  const cred = (await navigator.credentials.create({ publicKey })) as PublicKeyCredential | null
  if (!cred) throw new Error('Ingen credential returnerad — passkey-skapandet avbröts.')
  return credentialToJson(cred)
}

export async function getPasskeyAssertion(
  options: PublicKeyCredentialRequestOptionsJSON,
): Promise<string> {
  const publicKey = (
    PublicKeyCredential as unknown as {
      parseRequestOptionsFromJSON: (
        opts: PublicKeyCredentialRequestOptionsJSON,
      ) => PublicKeyCredentialRequestOptions
    }
  ).parseRequestOptionsFromJSON(options)

  const cred = (await navigator.credentials.get({ publicKey })) as PublicKeyCredential | null
  if (!cred) throw new Error('Ingen assertion returnerad — passkey-inloggningen avbröts.')
  return credentialToJson(cred)
}
